import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import cx from 'classnames';
import styles from './PrototypeModeIndicator.module.css';
import { isPrototypeEnabled } from './prototypeMode';
import {
  getPrototypeOverride,
  subscribePrototypeOverride,
} from './prototypeOverride';
import {
  getActivePrototypeSurfaceCount,
  subscribePrototypeSurfaces,
} from './prototypeRegistry';

// How close (px) the cursor must get to the border line or ribbon before the
// chrome yields. Wide enough to clear it before the pointer reaches an
// underlying control, narrow enough to leave the page centre untouched.
const HOT_ZONE_PADDING = 24;
// Debounce the fade-*in* so brushing past the edge doesn't flicker the chrome.
const RETURN_DELAY_MS = 400;

type PrototypeModeIndicatorProps = {
  label?: string;
};

const isNear = (
  rect: DOMRect | undefined,
  x: number,
  y: number,
  padding = HOT_ZONE_PADDING
) =>
  !!rect &&
  x >= rect.left - padding &&
  x <= rect.right + padding &&
  y >= rect.top - padding &&
  y <= rect.bottom + padding;

// The border is a rectangular outline: only the frame band conflicts with
// content, not the empty interior. Trigger on the ring, not the whole area.
const isOnBorderRing = (rect: DOMRect | undefined, x: number, y: number) => {
  if (!isNear(rect, x, y)) {
    return false;
  }
  const insideInner =
    x >= rect!.left + HOT_ZONE_PADDING &&
    x <= rect!.right - HOT_ZONE_PADDING &&
    y >= rect!.top + HOT_ZONE_PADDING &&
    y <= rect!.bottom - HOT_ZONE_PADDING;
  return !insideInner;
};

export function PrototypeModeIndicator({
  label = 'PROTOTYPE MODE',
}: PrototypeModeIndicatorProps) {
  const activePrototypeSurfaceCount = useSyncExternalStore(
    subscribePrototypeSurfaces,
    getActivePrototypeSurfaceCount,
    getActivePrototypeSurfaceCount
  );
  const override = useSyncExternalStore(
    subscribePrototypeOverride,
    getPrototypeOverride,
    getPrototypeOverride
  );

  const chromeVisible =
    isPrototypeEnabled &&
    override === 'default' &&
    activePrototypeSurfaceCount > 0;

  const borderRef = useRef<HTMLDivElement>(null);
  const ribbonRef = useRef<HTMLDivElement>(null);
  const [dodging, setDodging] = useState(false);

  useEffect(() => {
    if (!chromeVisible) {
      setDodging(false);
      return;
    }

    let frame = 0;
    let returnTimer = 0;
    let latest: { x: number; y: number } | null = null;

    const scheduleReturn = () => {
      if (returnTimer) {
        return;
      }
      returnTimer = window.setTimeout(() => {
        returnTimer = 0;
        setDodging(false);
      }, RETURN_DELAY_MS);
    };

    const evaluate = () => {
      frame = 0;
      if (!latest) {
        return;
      }
      const { x, y } = latest;
      const near =
        isOnBorderRing(borderRef.current?.getBoundingClientRect(), x, y) ||
        isNear(ribbonRef.current?.getBoundingClientRect(), x, y);

      if (near) {
        if (returnTimer) {
          window.clearTimeout(returnTimer);
          returnTimer = 0;
        }
        setDodging(true);
      } else {
        scheduleReturn();
      }
    };

    const onPointerMove = (event: PointerEvent) => {
      latest = { x: event.clientX, y: event.clientY };
      if (!frame) {
        frame = window.requestAnimationFrame(evaluate);
      }
    };

    // The cursor leaving the window stops firing move events; bring the
    // chrome back once it's clearly gone.
    const onPointerLeave = () => {
      latest = null;
      scheduleReturn();
    };

    window.addEventListener('pointermove', onPointerMove, { passive: true });
    document.addEventListener('pointerleave', onPointerLeave);

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerleave', onPointerLeave);
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
      if (returnTimer) {
        window.clearTimeout(returnTimer);
      }
    };
  }, [chromeVisible]);

  if (!chromeVisible) {
    return null;
  }

  return (
    <aside
      aria-label="Prototype mode is active"
      className={cx(styles.root, { [styles.dodging]: dodging })}
      data-testid="prototype-mode-indicator"
    >
      <div ref={borderRef} aria-hidden="true" className={styles.border} />
      <div ref={ribbonRef} aria-hidden="true" className={styles.ribbon}>
        {label}
      </div>
      <span className={styles.srOnly}>Prototype mode is active.</span>
    </aside>
  );
}
